import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { K } from "vitest/dist/chunks/reporters.d.BFLkQcL6.js";

type Vec = { x: number; y: number };

type Player = {
  id: string;
  type: "O" | "X";
  pos: Vec;
  route: Vec[];
  hasBall?: boolean;

  // defense-only
  role?: "rusher" | "coverage";
  followXId?: string; // coverage X follows a rusher
};

type BallState = {
  inFlight: boolean;
  pos: Vec;
  targetId: string | null;
  fromId: string | null;
  // optional speed override for soft tosses
  speed?: number;
};

/* ---------------- CONSTANTS ---------------- */

const countdownTime = 12;

const FIELD_WIDTH = 350;
const FIELD_HEIGHT = 600;

const ENDZONE_YARDS = 10;
const FIELD_YARDS = 100;
const TOTAL_YARDS = FIELD_YARDS + ENDZONE_YARDS * 2;
const PX_PER_YARD = FIELD_HEIGHT / TOTAL_YARDS;

const PLAYER_SPEED = 28;
const X_CHASE_SPEED = 30;

const BALL_SPEED = 250;
const BALL_OFFSET = { x: -3, y: -1 };
const SNAP_DIST = 3;

const PLAYER_RADIUS = 12;
const TACKLE_DIST = PLAYER_RADIUS * 2;
const BOUNCE_PUSH = 100;
const MAX_TACKLE_HITS = 3;

// ---- OUT OF BOUNDS / SAFETY ----
const SIDELINE_LEFT = 0 + PLAYER_RADIUS;
const SIDELINE_RIGHT = FIELD_WIDTH - PLAYER_RADIUS;
const HASH_OFFSET_FROM_CENTER = 50;2
const HASH_WIDTH = 6;
const HASH_HEIGHT = 2;

const SPEED_MULTIPLIERS = [
  1,
  1.25,
  1.5,
  2,
  3,
  4,
  5,
  10,
  20,
];

const FIELD_BOTTOM = FIELD_HEIGHT - PLAYER_RADIUS;

// bottom end zone lateral safety area
const BOT_ENDZONE_LEFT = SIDELINE_LEFT;
const BOT_ENDZONE_RIGHT = SIDELINE_RIGHT;

const O_FONT = 24;
const X_FONT = 22;

// ---- PLAYER VISUALS ----
const O_COLOR = "#956ddbff"; // neon purple
const O_GLOW =
  "0 0 1px rgba(255, 255, 255, 0.65), 0 0 2px rgba(167, 139, 250, 0.8), 0 0 2px rgba(206, 119, 247, 0.82)";
const X_COLOR = "#F43F5E"; // neon red/pink
const X_GLOW =
  "0 0 3px rgba(0,0,0,0.8), 0 0 18px rgba(244, 63, 94, 0.55), 0 0 30px rgba(239, 68, 68, 0.35)";
const OUTLINE = "0 0 2px rgba(0,0,0,0.85)";

const YARD_NUM_OFFSET_SMALL = 4;
const YARD_NUM_OFFSET_BIG = 20;

// ---- COVERAGE RADII ----
const MIKE_REACT_RADIUS = 75;
const SAFETY_REACT_RADIUS = 75;

// ---- FORMATION TUNING ----
const LINE_SPACING = 20;

// ---- OFFENSE ----
const OL_SPACING = 20;
const OL_Y = 0;

const WR_WIDE_X = 30;
const WR_Y = 5;

const SLOT_X = 90;
const SLOT_Y = 10;

const QB_X_OFFSET = 0;
const QB_Y = 33;

const RB_X_OFFSET = 20;
const RB_Y = 33;

// ---- DEFENSE ----
const DL_Y = 0;

const MIKE_X = -30;
const MIKE_Y = 70;

const LB_X = 90;
const LB_Y = 15;

const CB_X = 30;
const CB_Y = 35;

const SAFETY_X = 30;
const SAFETY_Y = 70;

// ---- PHYSICS / COLLISIONS ----
const BALL_RADIUS = 8;

const FAST_INTERCEPT_DIST = PLAYER_RADIUS * .9;
const LOB_INTERCEPT_RADIUS = PLAYER_RADIUS;

const COLLIDE_DIST = PLAYER_RADIUS * 2 * .9;
const COLLISION_ITERS = 2;

const QB_PRESSURE_DIST = COLLIDE_DIST + 6;
const TRENCH_DIST = PLAYER_RADIUS * 2 * 0.95;

const DL_FORWARD_FORCE = 0.6;
const DL_LATERAL_FORCE = 1.4;
const OL_WIN_CHANCE = 0.12;
const DL_WIN_CHANCE = 0.18;
const OL_ANCHOR_STRENGTH = 0.15;

const COVERAGE_MIN_DIST = PLAYER_RADIUS * 2 * .9;
const COVERAGE_SEPARATION_FORCE = 0.35;

/* ---------------- DERIVED FIELD GEOMETRY ---------------- */

function yFromYards(yardsFromTop: number) {
  return yardsFromTop * PX_PER_YARD;
}

const TOP_ENDZONE_Y = yFromYards(ENDZONE_YARDS);
const BOT_ENDZONE_Y = yFromYards(ENDZONE_YARDS + FIELD_YARDS);

const LOS_BASE_Y = FIELD_HEIGHT - yFromYards(ENDZONE_YARDS) - 60;
const OFFENSE_Y = LOS_BASE_Y + 12;
const DEFENSE_Y = LOS_BASE_Y - 12;

// true LOS halfway between the two lines
const LOS_Y = (OFFENSE_Y + DEFENSE_Y) / 2;

// ---- DEFENSE VARIATION ----
type DefenseMode = "man" | "zone";


const BLITZ_CHANCE = 0.6; // chance to send 1 extra rusher from coverage
const MAN_CB_CUSHION = 18;
const MAN_LB_CUSHION = 22;
const MAN_S_CUSHION = 45;

const ZONE_DROP_SPEED = 0.85; // defenders move a bit slower in zone
const ZONE_DEPTH_SAFETY = 110;
const ZONE_DEPTH_LB = 70;

/* ---------------- HELPERS ---------------- */

function normalize(v: Vec): Vec {
  const m = Math.hypot(v.x, v.y);
  return m > 0.0001 ? { x: v.x / m, y: v.y / m } : { x: 0, y: 0 };
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function dist(a: Vec, b: Vec) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// smooth target (prevents twitching)
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
function lerpVec(a: Vec, b: Vec, t: number): Vec {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}


function isOutOfBoundsX(x: number) {
  return x <= SIDELINE_LEFT || x >= SIDELINE_RIGHT;
}

function isOutBottom(y: number) {
  return y >= FIELD_BOTTOM;
}

function isInBottomEndzone(y: number) {
  return y >= BOT_ENDZONE_Y;
}

function findPlayer(players: Player[], id: string) {
  return players.find((p) => p.id === id) ?? null;
}

function yardLabelFromFieldYards(fieldYardsFromTop: number) {
  const n = fieldYardsFromTop;
  if (n === 50) return 50;
  if (n < 50) return n;
  return 100 - n;
}

function isWR(p: Player) {
  return p.type === "O" && p.route.length > 0;
}

function isDBorLB(p: Player) {
  return p.type === "X" && p.role === "coverage";
}

function isOffensiveLinemen(p: Player) {
  return p.type === "O" && p.id.startsWith("o");
}

function isDefensiveLineman(p: Player) {
  return p.type === "X" && p.role === "rusher";
}

function isBallTouchingTopGoalLine(ballY: number) {
  return ballY - BALL_RADIUS <= TOP_ENDZONE_Y;
}

/* ---------------- FORMATIONS ---------------- */

function createOffense(): Player[] {
  const cx = FIELD_WIDTH / 2;

  return [
    // ---- OFFENSIVE LINE ----
    { id: "oLT", type: "O", pos: { x: cx - OL_SPACING * 2, y: OFFENSE_Y + OL_Y }, route: [] },
    { id: "oLG", type: "O", pos: { x: cx - OL_SPACING, y: OFFENSE_Y + OL_Y }, route: [] },
    { id: "oC", type: "O", pos: { x: cx, y: OFFENSE_Y + OL_Y }, route: [], hasBall: true },
    { id: "oRG", type: "O", pos: { x: cx + OL_SPACING, y: OFFENSE_Y + OL_Y }, route: [] },
    { id: "oRT", type: "O", pos: { x: cx + OL_SPACING * 2, y: OFFENSE_Y + OL_Y }, route: [] },

    // ---- QB ----
    {
      id: "oQB",
      type: "O",
      pos: { x: cx + QB_X_OFFSET, y: OFFENSE_Y + QB_Y },
      route: [],
      hasBall: true,
    },

    // ---- WIDE RECEIVERS ----
    { id: "oWRL", type: "O", pos: { x: WR_WIDE_X, y: OFFENSE_Y + WR_Y }, route: [] },
    { id: "oWRR", type: "O", pos: { x: FIELD_WIDTH - WR_WIDE_X, y: OFFENSE_Y + WR_Y }, route: [] },

    // ---- SLOT RECEIVERS ----
    { id: "oSlotL", type: "O", pos: { x: cx - SLOT_X, y: OFFENSE_Y + SLOT_Y }, route: [] },
    { id: "oSlotR", type: "O", pos: { x: cx + SLOT_X, y: OFFENSE_Y + SLOT_Y }, route: [] },

    // ---- RUNNING BACK ----
    { id: "oRB", type: "O", pos: { x: cx + RB_X_OFFSET, y: OFFENSE_Y + RB_Y }, route: [] },
  ];
}

function createDefense(): Player[] {
  const cx = FIELD_WIDTH / 2;

  return [
    // ---- DEFENSIVE LINE (rushers) ----
    { id: "xDEL", type: "X", role: "rusher", pos: { x: cx - LINE_SPACING * 2, y: DEFENSE_Y - DL_Y }, route: [] },
    { id: "xDTL", type: "X", role: "rusher", pos: { x: cx - LINE_SPACING, y: DEFENSE_Y - DL_Y }, route: [] },
    { id: "xNT", type: "X", role: "rusher", pos: { x: cx, y: DEFENSE_Y - DL_Y }, route: [] },
    { id: "xDTR", type: "X", role: "rusher", pos: { x: cx + LINE_SPACING, y: DEFENSE_Y - DL_Y }, route: [] },
    { id: "xDER", type: "X", role: "rusher", pos: { x: cx + LINE_SPACING * 2, y: DEFENSE_Y - DL_Y }, route: [] },

    // ---- LINEBACKERS ----
    { id: "xLB1", type: "X", role: "coverage", pos: { x: cx - LB_X, y: DEFENSE_Y - LB_Y }, route: [] },
    { id: "xLB2", type: "X", role: "coverage", pos: { x: cx + MIKE_X, y: DEFENSE_Y - MIKE_Y }, route: [] }, // MIKE
    { id: "xLB3", type: "X", role: "coverage", pos: { x: cx + LB_X, y: DEFENSE_Y - LB_Y }, route: [] },

    // ---- CORNERBACKS ----
    { id: "xCBL", type: "X", role: "coverage", pos: { x: CB_X, y: DEFENSE_Y - CB_Y }, route: [] },
    { id: "xCBR", type: "X", role: "coverage", pos: { x: FIELD_WIDTH - CB_X, y: DEFENSE_Y - CB_Y }, route: [] },

    // ---- SAFETY ----
    { id: "xS", type: "X", role: "coverage", pos: { x: cx + SAFETY_X, y: DEFENSE_Y - SAFETY_Y }, route: [] },
  ];
}

function createFormation(): Player[] {
  return [...createOffense(), ...createDefense()];
}

function assignCoverageTargets(players: Player[]): Player[] {
  const rushers = players.filter((p) => p.type === "X" && p.role === "rusher");
  if (!rushers.length) return players;

  return players.map((p) => {
    if (p.type !== "X" || p.role !== "coverage") return p;

    let nearest = rushers[0];
    let best = Infinity;

    for (const r of rushers) {
      const d = dist(p.pos, r.pos);
      if (d < best) {
        best = d;
        nearest = r;
      }
    }

    return { ...p, followXId: nearest.id };
  });
}

/* ---------------- COLLISION / SPACING ---------------- */

function resolveOpposingCollisions(input: Player[]) {
  let ps = input.map((p) => ({ ...p, pos: { ...p.pos } }));

  const MAX_PUSH = PLAYER_RADIUS * 0.5;

  for (let iter = 0; iter < COLLISION_ITERS; iter++) {
    for (let i = 0; i < ps.length; i++) {
      for (let j = 0; j < ps.length; j++) {
        if (i === j) continue;

        const a = ps[i];
        const b = ps[j];

        // trench only: OL vs DL
        if (!isOffensiveLinemen(a) || !isDefensiveLineman(b)) continue;

        const dx = b.pos.x - a.pos.x;
        const dy = b.pos.y - a.pos.y;
        const d = Math.hypot(dx, dy);
        if (d <= 0.001 || d >= TRENCH_DIST) continue;

        const nx = dx / d;
        const ny = dy / d;

        const overlap = Math.min(TRENCH_DIST - d, MAX_PUSH);

        // Stable lateral direction
        const lateralDir = Math.sign(dx) || 1;
        const lx = -ny * lateralDir;
        const ly = nx * lateralDir;

        const roll = Math.random();

        // DL WINS CLEAN (rare)
        if (roll < DL_WIN_CHANCE) {
          // DL moves forward
          b.pos.x = clamp(b.pos.x + nx * overlap * DL_FORWARD_FORCE, 8, FIELD_WIDTH - 8);
          b.pos.y = clamp(b.pos.y + ny * overlap * DL_FORWARD_FORCE, 8, FIELD_HEIGHT - 8);

          // OL gives ground
          a.pos.x = clamp(a.pos.x - nx * overlap * 0.6, 8, FIELD_WIDTH - 8);
          a.pos.y = clamp(a.pos.y - ny * overlap * 0.6, 8, FIELD_HEIGHT - 8);
        }

        // OL WINS REP (drive sideways)
        else if (roll < DL_WIN_CHANCE + OL_WIN_CHANCE) {
          // DL is displaced sideways, NO forward progress
          b.pos.x = clamp(b.pos.x + lx * overlap * DL_LATERAL_FORCE, 8, FIELD_WIDTH - 8);
          b.pos.y = clamp(b.pos.y + ly * overlap * DL_LATERAL_FORCE, 8, FIELD_HEIGHT - 8);
        }

        // STALEMATE (most common)
        else {
          // HARD STOP: cancel forward movement
          // Only lateral micro-adjustments allowed
          b.pos.x = clamp(b.pos.x + lx * overlap * 0.25, 8, FIELD_WIDTH - 8);
          b.pos.y = clamp(b.pos.y + ly * overlap * 0.25, 8, FIELD_HEIGHT - 8);

          // OL anchors firmly
          a.pos.x = clamp(a.pos.x - nx * overlap * OL_ANCHOR_STRENGTH * 1.6, 8, FIELD_WIDTH - 8);
          a.pos.y = clamp(a.pos.y - ny * overlap * OL_ANCHOR_STRENGTH * 1.6, 8, FIELD_HEIGHT - 8);
        }

        ps[i] = a;
        ps[j] = b;
      }
    }
  }

  return ps;
}

function applySoftSeparation(players: Player[], ballCarrierId?: string): Player[] {
  const next = players.map((p) => ({ ...p, pos: { ...p.pos } }));

  // accumulate separation impulses
  const impulses: Record<string, Vec> = {};
  for (const p of next) impulses[p.id] = { x: 0, y: 0 };

  const MAX_PUSH = 2; // hard cap per frame (prevents teleport)

  for (let i = 0; i < next.length; i++) {
    const a = next[i];

    for (let j = i + 1; j < next.length; j++) {
      const b = next[j];

      // 🚫 NEVER separate defender from ball carrier
      if (
        ballCarrierId &&
        (
          (a.id === ballCarrierId && b.type === "X") ||
          (b.id === ballCarrierId && a.type === "X")
        )
      ) {
        continue;
      }

      // skip DB/LB vs WR (no holding)
      const isCoverageVsWR =
        (isWR(a) && isDBorLB(b)) || (isWR(b) && isDBorLB(a));
      if (isCoverageVsWR) continue;

      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      const d = Math.hypot(dx, dy);
      if (d <= 0.001) continue;

      const minDist = PLAYER_RADIUS * 2 * .85;
      if (d >= minDist) continue;

      const nx = dx / d;
      const ny = dy / d;
      const overlap = minDist - d;

      const push = Math.min(overlap * 0.6, MAX_PUSH);

      impulses[a.id].x -= nx * push;
      impulses[a.id].y -= ny * push;
      impulses[b.id].x += nx * push;
      impulses[b.id].y += ny * push;
    }
  }


  // apply impulses once
  for (const p of next) {
    const imp = impulses[p.id];
    if (!imp) continue;

    p.pos.x = clamp(p.pos.x + imp.x, 8, FIELD_WIDTH - 8);
    p.pos.y = clamp(p.pos.y + imp.y, 8, FIELD_HEIGHT - 8);
  }

  return next;
}

function applyCoverageSpacing(players: Player[]): Player[] {
  const next = players.map((p) => ({ ...p, pos: { ...p.pos } }));

  for (const wr of next) {
    if (!isWR(wr)) continue;

    for (const db of next) {
      if (!isDBorLB(db)) continue;

      const dx = db.pos.x - wr.pos.x;
      const dy = db.pos.y - wr.pos.y;
      const d = Math.hypot(dx, dy);
      if (d <= 0.001) continue;

      if (d < COVERAGE_MIN_DIST) {
        const nx = dx / d;
        const ny = dy / d;
        const push = (COVERAGE_MIN_DIST - d) * COVERAGE_SEPARATION_FORCE;

        // only move defender
        db.pos.x = clamp(db.pos.x + nx * push, 8, FIELD_WIDTH - 8);
        db.pos.y = clamp(db.pos.y + ny * push, 8, FIELD_HEIGHT - 8);
      }
    }
  }

  return next;
}

/* ---------------- UI HELPERS ---------------- */

function pad3(n: number) {
  const s = String(Math.max(0, Math.floor(n)));
  return s.length >= 3 ? s : "0".repeat(3 - s.length) + s;
}

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

/* ---------------- COMPONENT ---------------- */

export default function CalceraBallPanel() {
  const fieldRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const losYRef = useRef<number>(LOS_Y);

  const lastTime = useRef<number | null>(null);
  const clickTimerRef = useRef<number | null>(null);
  const clickCountRef = useRef(0);
  const snapImmunityRef = useRef(true);
  const hasBallCrossedLOSRef = useRef(false);
  const [speedLevel, setSpeedLevel] = useState(0);
  const defenseModeRef = useRef<DefenseMode>("man");
  const extraBlitzerRef = useRef<string | null>(null);
  const tackleLockRef = useRef(false);

  // for man: defenderId -> offenseId
  const manAssignRef = useRef<Record<string, string>>({});

  // for zone: defenderId -> landmark (static target that updates a bit)
  const zoneLandmarkRef = useRef<Record<string, Vec>>({});

  const hitFlashRef = useRef<Record<string, number>>({});

  const resetPlayRefs = () => {
  hasBallCrossedLOSRef.current = false;
  tackleHitsRef.current = 0;
  lastHitTimeRef.current = 0;
  playOutcomeRef.current = "none";
  tackleLockRef.current = false;

  bounceImpulseRef.current = {};
};


  // ---- BOUNCE IMPULSE ----
  const BOUNCE_DAMPING = 26; // higher = slower bounce
  const MAX_BOUNCE_STEP = PLAYER_RADIUS * 0.10;

  const HOLD_TO_DRAW_MS = 150;
  const DOUBLE_CLICK_MS = 250;

  const tackleHitsRef = useRef(0);
  const lastHitTimeRef = useRef(0);

  type PlayOutcome = "none" | "tackle" | "interception" | "touchdown" | "turnover" | "safety";
  const playOutcomeRef = useRef<PlayOutcome>("none");

  const drawingLockRef = useRef<string | null>(null);
  const pressStartRef = useRef<number | null>(null);
  const pressPlayerRef = useRef<string | null>(null);

  // Slow bounce impulses applied over time (prevents teleporting)
  const bounceImpulseRef = useRef<Record<string, Vec>>({});

  const MAX_LIVES = 3;
  const MAX_DOWNS = 4;

  const [running, setRunning] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [playLive, setPlayLive] = useState(false);
  const [drawingFor, setDrawingFor] = useState<string | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [lives, setLives] = useState(MAX_LIVES);
  const [down, setDown] = useState(1);
  const [score, setScore] = useState(0);

  const [showInterceptionUI, setShowInterceptionUI] = useState(false);
  const [showSafetyUI, setShowSafetyUI] = useState(false);
  const [showScoreUI, setShowScoreUI] = useState(false);

  const [players, setPlayers] = useState<Player[]>(() => createFormation());
  const initialPlayersRef = useRef<Player[]>([]);
  const playersRef = useRef<Player[]>(players);
  
  const [ball, setBall] = useState<BallState>(() => {
    const center = initialPlayersRef.current.find((p) => p.id === "oC");
    return {
      inFlight: false,
      pos: center
        ? { x: center.pos.x + BALL_OFFSET.x, y: center.pos.y + BALL_OFFSET.y }
        : { x: FIELD_WIDTH / 2, y: FIELD_HEIGHT / 2 },
      targetId: null,
      fromId: null,
    };
  });
  const ballRef = useRef<BallState>(ball);

  const commitPlayers = useCallback((next: Player[]) => {
    playersRef.current = next;
    setPlayers(next);
  }, []);

  /* ---------------- INIT / REFS ---------------- */

  useEffect(() => {
    const fresh = createFormation();
    const normalized = fresh.map((p) => ({
      ...p,
      pos: { ...p.pos },
      route: [],
      hasBall: p.hasBall,
    }));

    initialPlayersRef.current = normalized;
    playersRef.current = normalized;
    setPlayers(normalized);
  }, [
    // offense
    OL_SPACING,
    OL_Y,
    WR_WIDE_X,
    WR_Y,
    SLOT_X,
    SLOT_Y,
    QB_X_OFFSET,
    QB_Y,
    RB_X_OFFSET,
    RB_Y,
    // defense
    DL_Y,
    MIKE_X,
    MIKE_Y,
    LB_X,
    LB_Y,
    CB_X,
    CB_Y,
    SAFETY_X,
    SAFETY_Y,
  ]);

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    ballRef.current = ball;
  }, [ball]);

  const ballCarrier = useMemo(() => players.find((p) => p.hasBall) || null, [players]);
  const speedMultiplier = useMemo(
    () => SPEED_MULTIPLIERS[speedLevel] ?? 1,
    [speedLevel]
  );

  const countdownDisplay = useMemo(() => {
  if (countdown === null) return countdownTime; // idle
  if (countdown === 0) return "GO";
  return countdown;
}, [countdown]);

  /* ---------------- UI TIMEOUTS ---------------- */

  useEffect(() => {
    if (!showSafetyUI) return;
    const t = window.setTimeout(() => setShowSafetyUI(false), 1500);
    return () => window.clearTimeout(t);
  }, [showSafetyUI]);

  useEffect(() => {
    if (!showScoreUI) return;
    const t = window.setTimeout(() => setShowScoreUI(false), 1800);
    return () => window.clearTimeout(t);
  }, [showScoreUI]);

  useEffect(() => {
    if (!showInterceptionUI) return;
    const t = window.setTimeout(() => setShowInterceptionUI(false), 1500);
    return () => window.clearTimeout(t);
  }, [showInterceptionUI]);

  /* ---------------- THROW / TOSS ---------------- */

  const tryThrowTo = useCallback(
    (targetId: string, speed: "fast" | "soft" = "fast") => {
      const carrier = playersRef.current.find((p) => p.hasBall);
      if (!carrier) return;
      if (ballRef.current.inFlight) return;
      if (!running) return;

      const target = playersRef.current.find((p) => p.id === targetId);
      if (!target || target.type !== "O" || target.id === carrier.id) return;

      // THROW DIRECTION RULE
      const dy = target.pos.y - carrier.pos.y;
      const isBackwardThrow = dy > 0;

      // once ball crosses LOS, NO throws allowed
      if (hasBallCrossedLOSRef.current) return;

      // before LOS: allow both forward & backward throws

      setBall({
        inFlight: true,
        fromId: carrier.id,
        targetId: target.id,
        pos: { x: carrier.pos.x + BALL_OFFSET.x, y: carrier.pos.y + BALL_OFFSET.y },
        speed: speed === "soft" ? BALL_SPEED * 0.3 : BALL_SPEED,
      });
    },
    [running]
  );

  /* ---------------- CLICK / HOLD HANDLING ---------------- */

  useEffect(() => {
    const onUp = () => {
      const start = pressStartRef.current;
      const targetId = pressPlayerRef.current;

      pressStartRef.current = null;
      pressPlayerRef.current = null;

      // If we were drawing, end drawing and stop here
      if (drawingLockRef.current) {
        drawingLockRef.current = null;
        setDrawingFor(null);
        return;
      }

      // Cancel pending hold
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }

      if (!start || !targetId) return;

      const heldMs = performance.now() - start;
      if (heldMs > HOLD_TO_DRAW_MS) return;

      clickCountRef.current += 1;

      window.setTimeout(() => {
        if (clickCountRef.current === 1) tryThrowTo(targetId, "soft");
        else if (clickCountRef.current >= 2) tryThrowTo(targetId, "fast");
        clickCountRef.current = 0;
      }, DOUBLE_CLICK_MS);
    };

    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [tryThrowTo]);


const initDefenseCall = useCallback((ps: Player[]) => {
  // 50/50 man/zone each play
  defenseModeRef.current = Math.random() < 0.5 ? "man" : "zone";

  // choose 1 extra blitzer from coverage some % of the time
  const coverage = ps.filter((p) => p.type === "X" && p.role === "coverage");
  extraBlitzerRef.current =
    coverage.length && Math.random() < BLITZ_CHANCE
      ? coverage[Math.floor(Math.random() * coverage.length)].id
      : null;

  // MAN assignments (simple but effective):
  // CBs -> WRs, LBs -> slots/RB/QB, S -> "best remaining / help"
  const oWRL = ps.find((p) => p.id === "oWRL");
  const oWRR = ps.find((p) => p.id === "oWRR");
  const oSlotL = ps.find((p) => p.id === "oSlotL");
  const oSlotR = ps.find((p) => p.id === "oSlotR");
  const oRB = ps.find((p) => p.id === "oRB");
  const oQB = ps.find((p) => p.id === "oQB");

  const xCBL = ps.find((p) => p.id === "xCBL");
  const xCBR = ps.find((p) => p.id === "xCBR");
  const xLB1 = ps.find((p) => p.id === "xLB1");
  const xLB2 = ps.find((p) => p.id === "xLB2");
  const xLB3 = ps.find((p) => p.id === "xLB3");
  const xS = ps.find((p) => p.id === "xS");

  const man: Record<string, string> = {};
  if (xCBL && oWRL) man[xCBL.id] = oWRL.id;
  if (xCBR && oWRR) man[xCBR.id] = oWRR.id;

  // LBs match inside guys
  const inside = [oSlotL, oSlotR, oRB, oQB].filter(Boolean) as Player[];
  const lbs = [xLB1, xLB2, xLB3].filter(Boolean) as Player[];

  for (let i = 0; i < lbs.length; i++) {
    const def = lbs[i];
    const off = inside[i % inside.length];
    if (def && off) man[def.id] = off.id;
      }

      // Safety "man" is actually help: shade strongest threat (closest to endzone / deepest)
      if (xS) {
        const threats = [oWRL, oWRR, oSlotL, oSlotR].filter(Boolean) as Player[];
        let best = threats[0];
        let bestScore = -Infinity;
        for (const t of threats) {
          // reward depth (smaller y is deeper toward top endzone)
          const score = -t.pos.y + Math.abs(t.pos.x - FIELD_WIDTH / 2) * 0.15;
          if (score > bestScore) {
            bestScore = score;
            best = t;
          }
        }
        if (best) man[xS.id] = best.id;
      }

      manAssignRef.current = man;

      // ZONE landmarks (simple cover-3-ish):
      // CBs outside thirds, S deep middle, LBs hook zones
      const cx = FIELD_WIDTH / 2;
      zoneLandmarkRef.current = {
        xCBL: { x: FIELD_WIDTH * 0.18, y: DEFENSE_Y - ZONE_DEPTH_SAFETY * 0.72 },
        xCBR: { x: FIELD_WIDTH * 0.82, y: DEFENSE_Y - ZONE_DEPTH_SAFETY * 0.72 },
        xS: { x: cx, y: DEFENSE_Y - ZONE_DEPTH_SAFETY },
        xLB1: { x: cx - 70, y: DEFENSE_Y - ZONE_DEPTH_LB },
        xLB2: { x: cx, y: DEFENSE_Y - (ZONE_DEPTH_LB + 12) },
        xLB3: { x: cx + 70, y: DEFENSE_Y - ZONE_DEPTH_LB },
      } as any;
    }, []);


  /* ---------------- COUNTDOWN (HIKE) ---------------- */

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      setCountdown(null);

      snapImmunityRef.current = true;

      playersRef.current = playersRef.current.map((p) =>
        p.id === "oQB" ? { ...p, hasBall: true } : p.id === "oC" ? { ...p, hasBall: false } : p
      );

      setBall({
        inFlight: false,
        pos: {
          x: playersRef.current.find((p) => p.id === "oQB")!.pos.x + BALL_OFFSET.x,
          y: playersRef.current.find((p) => p.id === "oQB")!.pos.y + BALL_OFFSET.y,
        },
        targetId: null,
        fromId: null,
      });

      setTimeout(() => {
        snapImmunityRef.current = false;
      }, 50);

      playersRef.current = assignCoverageTargets(playersRef.current);
      initDefenseCall(playersRef.current);
      setPlayers(playersRef.current);

      setPlayLive(true);
      lastTime.current = null;
      return;
    }

    const t = window.setTimeout(() => {
      setCountdown((c) => (c !== null ? c - 1 : c));
    }, 800);

    return () => window.clearTimeout(t);
  }, [countdown]);

  /**
   * Ends the play immediately, but we still finish the current animation frame.
   */
  const endPlayImmediate = useCallback((outcome: PlayOutcome) => {
    playOutcomeRef.current = outcome;
    setRunning(false);
    setPlayLive(false);
    setCountdown(null);
    setDrawingFor(null);
    lastTime.current = null;
  }, []);

  /* ---------------- RESET HELPERS ---------------- */

  const resetToStart = useCallback(() => {
    setGameOver(false);
    resetPlayRefs();
    setRunning(false);
    setPlayLive(false);
    setCountdown(null);
    setDrawingFor(null);
    lastTime.current = null;
    losYRef.current = LOS_Y;
    const restored = initialPlayersRef.current.map((p) => ({
      ...p,
      pos: { ...p.pos },
      route: [],
      hasBall: p.hasBall,
    }));

    playersRef.current = restored;
    setPlayers(restored);

    const carrier =
      restored.find((p) => p.hasBall) ?? restored.find((p) => p.id === "oQB") ?? null;

    setBall({
      inFlight: false,
      pos: carrier
        ? { x: carrier.pos.x + BALL_OFFSET.x, y: carrier.pos.y + BALL_OFFSET.y }
        : { x: FIELD_WIDTH / 2, y: FIELD_HEIGHT / 2 },
      targetId: null,
      fromId: null,
    });
  }, []);

  const loseLifeAndReset = useCallback(() => {
    resetPlayRefs();

    setLives((l) => {
      const next = Math.max(0, l - 1);
      if (next === 0) {
        setGameOver(true);
        setRunning(false);
        setPlayLive(false);
        setCountdown(null);
      }
      return next;
    });

    setDown(1);
    resetToStart();

  }, [resetToStart]);


  const endPlayAt = useCallback((yStop: number) => {
    resetPlayRefs();
    setRunning(false);
    setPlayLive(false);
    setCountdown(null);
    setDrawingFor(null);
    lastTime.current = null;
    losYRef.current = clamp(yStop, 8, FIELD_HEIGHT - 8);

    const originalCarrier = initialPlayersRef.current.find((p) => p.hasBall);
    if (!originalCarrier) return;

    const deltaY = yStop - originalCarrier.pos.y;

    const shiftedPlayers = initialPlayersRef.current.map((p) => ({
      ...p,
      pos: { x: p.pos.x, y: clamp(p.pos.y + deltaY, 8, FIELD_HEIGHT - 8) },
      route: [],
      hasBall: p.hasBall,
    }));

    playersRef.current = shiftedPlayers;
    setPlayers(shiftedPlayers);

    setBall({
      inFlight: false,
      pos: {
        x: originalCarrier.pos.x,
        y: clamp(originalCarrier.pos.y + deltaY, 8, FIELD_HEIGHT - 8),
      },
      targetId: null,
      fromId: null,
    });
  
  }, []);

  /* ---------------- MOVEMENT + BALL LOOP ---------------- */

  useEffect(() => {
    
    if (!running) return;

    let raf = 0;

    const loop = (time: number) => {
      if (lastTime.current == null) lastTime.current = time;
      const dt = (time - lastTime.current) / 1000;
      lastTime.current = time;

      const carrierNow = playersRef.current.find((p) => p.hasBall) || null;

      const bPrev = ballRef.current;
      let nextBall: BallState = bPrev;

      let forceEndThisFrame = false;
      let ballTakenThisFrame = false;

      // keep RAF alive during countdown, but do not move
      if (!playLive) {
        raf = requestAnimationFrame(loop);
        return;
      }

      // 0) soft separation BEFORE movement
      let nextPlayers =
      tackleLockRef.current
        ? playersRef.current.map((p) => ({ ...p, pos: { ...p.pos } }))
        : applySoftSeparation(playersRef.current, carrierNow?.id);

      // 1) move players
      nextPlayers = nextPlayers.map((p) => {
        // freeze the player you are currently drawing a route for
        if (drawingLockRef.current === p.id) return { ...p, pos: p.pos };

        // defense movement
        if (p.type === "X") {
          const b = ballRef.current;
          const carrier = carrierNow;

          const blitzAllowed = !hasBallCrossedLOSRef.current && !b.inFlight;

          // past LOS and ball not in flight: rally to ball carrier
          const pastLOS = hasBallCrossedLOSRef.current && !b.inFlight && carrier !== null;

          // 🔥 HARD OVERRIDE: after catch + past LOS, everyone rallies
          if (pastLOS && carrier) {
            const dx = carrier.pos.x - p.pos.x;
            const dy = carrier.pos.y - p.pos.y;
            const d = Math.hypot(dx, dy);
            if (d < 1) return p;

            return {
              ...p,
              pos: {
                x: clamp(
                  p.pos.x + (dx / d) * X_CHASE_SPEED * speedMultiplier * dt,
                  8,
                  FIELD_WIDTH - 8
                ),
                y: clamp(
                  p.pos.y + (dy / d) * X_CHASE_SPEED * speedMultiplier * dt,
                  8,
                  FIELD_HEIGHT - 8
                ),
              },
            };
          }

          // extra blitzer: always rush QB/ball
          const isExtraBlitzer =
            blitzAllowed && extraBlitzerRef.current === p.id;

          // RUSHERS: always chase QB/ball (as before)
          if (p.role === "rusher" || isExtraBlitzer) {
            const engaged = playersRef.current.some(
              (o) => isOffensiveLinemen(o) && dist(o.pos, p.pos) < TRENCH_DIST * 1.02
            );

            // when ball in flight, you can chase ball; otherwise chase QB until LOS crossed, then carrier
            const qb = findPlayer(playersRef.current, "oQB");
            const rushTarget =
              b.inFlight
                ? b.pos
                : pastLOS
                ? carrier!.pos
                : qb?.pos ?? carrier?.pos ?? p.pos;

            const dx = rushTarget.x - p.pos.x;
            const dy = rushTarget.y - p.pos.y;
            const d = Math.hypot(dx, dy);
            if (d < 1) return p;

            const speed = (engaged ? X_CHASE_SPEED * 0.18 : X_CHASE_SPEED) * speedMultiplier;
            return {
              ...p,
              pos: {
                x: clamp(p.pos.x + (dx / d) * speed * dt, 8, FIELD_WIDTH - 8),
                y: clamp(p.pos.y + (dy / d) * speed * dt, 8, FIELD_HEIGHT - 8),
              },
            };
          }

          // COVERAGE defenders (more defensive)
          const mode = defenseModeRef.current;

          // if ball is in air OR carrier crossed LOS: everyone closes (but still with cushion)
          const closeNow = b.inFlight || pastLOS;

          // MAN: follow assignment with cushion (LB/S sit deeper than CB)
          if (mode === "man") {
            const markId = manAssignRef.current[p.id];
            const mark = markId ? findPlayer(playersRef.current, markId) : null;

            if (!mark) return p;

            // cushion: defenders sit "above" (toward top endzone) the receiver
            let cushion = MAN_CB_CUSHION;
            if (p.id.startsWith("xLB")) cushion = MAN_LB_CUSHION;
            if (p.id === "xS") cushion = MAN_S_CUSHION;

            const desired: Vec = closeNow
              ? mark.pos // close when ball/crossed
              : { x: mark.pos.x, y: mark.pos.y - cushion };

            const dx = desired.x - p.pos.x;
            const dy = desired.y - p.pos.y;
            const d = Math.hypot(dx, dy);
            if (d < 1) return p;

            const speed = X_CHASE_SPEED * (closeNow ? 1 : 0.78) * speedMultiplier;
            return {
              ...p,
              pos: {
                x: clamp(p.pos.x + (dx / d) * speed * dt, 8, FIELD_WIDTH - 8),
                y: clamp(p.pos.y + (dy / d) * speed * dt, 8, FIELD_HEIGHT - 8),
              },
            };
          }

          // ZONE: drift to landmark, then break on nearest threat when ball is thrown / crossed
          const landmark =
            (zoneLandmarkRef.current as any)[p.id] ??
            { x: FIELD_WIDTH / 2, y: DEFENSE_Y - 55 };

          // update landmark slightly based on ball x (zone slides)
          const slide = clamp((b.pos.x - FIELD_WIDTH / 2) * 0.18, -45, 45);
          const slidLandmark = { x: clamp(landmark.x + slide, 8, FIELD_WIDTH - 8), y: landmark.y };

          let desired: Vec = slidLandmark;

          if (closeNow) {
            // break on nearest eligible receiver, but keep LBs/S from over-committing instantly
            const elig = playersRef.current.filter(
              (o) => o.type === "O" && !o.id.startsWith("oL") && o.id !== "oC"
            );

            let nearest = elig[0] ?? null;
            let best = Infinity;
            for (const o of elig) {
              const d0 = dist(p.pos, o.pos);
              if (d0 < best) {
                best = d0;
                nearest = o;
              }
            }

            if (nearest) {
              const cushion = p.id === "xS" ? 28 : p.id.startsWith("xLB") ? 16 : 10;
              desired = { x: nearest.pos.x, y: nearest.pos.y - cushion };
            }
          }

          // smooth target so zone looks “smart”
          const smoothDesired = lerpVec(p.pos, desired, 0.22);

          const dx = smoothDesired.x - p.pos.x;
          const dy = smoothDesired.y - p.pos.y;
          const d = Math.hypot(dx, dy);
          if (d < 1) return p;

          const speed = X_CHASE_SPEED * ZONE_DROP_SPEED * (closeNow ? 0.95 : 0.72) * speedMultiplier;
          return {
            ...p,
            pos: {
              x: clamp(p.pos.x + (dx / d) * speed * dt, 8, FIELD_WIDTH - 8),
              y: clamp(p.pos.y + (dy / d) * speed * dt, 8, FIELD_HEIGHT - 8),
            },
          };
        }

        // offense movement
        if (!p.route.length) return p;

        const target = p.route[0];
        const dx = target.x - p.pos.x;
        const dy = target.y - p.pos.y;
        const d = Math.hypot(dx, dy);

        if (d < SNAP_DIST) return { ...p, route: p.route.slice(1) };

        return {
          ...p,
          pos: {
            x: clamp(p.pos.x + (dx / d) * PLAYER_SPEED * speedMultiplier * dt, 8, FIELD_WIDTH - 8),
            y: clamp(p.pos.y + (dy / d) * PLAYER_SPEED * speedMultiplier * dt, 8, FIELD_HEIGHT - 8),
          },
        };
      });

      const carrierNext = nextPlayers.find((p) => p.hasBall);

      // QB pressure pick (soft throws)
      const bPrevPressure = ballRef.current;
      const isSoftPressureThrow =
        bPrevPressure.inFlight && (bPrevPressure.speed ?? BALL_SPEED) < BALL_SPEED;

      if (isSoftPressureThrow) {
        const thrower = nextPlayers.find((p) => p.id === bPrevPressure.fromId);
        if (thrower) {
          const qbInterceptor = nextPlayers.find(
            (p) => p.type === "X" && dist(p.pos, thrower.pos) <= QB_PRESSURE_DIST
          );

          if (qbInterceptor) {
            nextPlayers = nextPlayers.map((p) =>
              p.id === qbInterceptor.id ? { ...p, hasBall: true } : p.hasBall ? { ...p, hasBall: false } : p
            );

            nextBall = {
              inFlight: false,
              pos: { x: qbInterceptor.pos.x, y: qbInterceptor.pos.y },
              targetId: null,
              fromId: null,
            };

            setShowInterceptionUI(true);
            endPlayImmediate("interception");

            forceEndThisFrame = true;
            ballTakenThisFrame = true;
          }
        }
      }

      if (!forceEndThisFrame) {
        // trench collisions
        nextPlayers = resolveOpposingCollisions(nextPlayers);

        // coverage spacing (CB/LB vs WR)
        nextPlayers = applyCoverageSpacing(nextPlayers);

        // ball logic
        if (!bPrev.inFlight) {
          const c = nextPlayers.find((p) => p.hasBall) || null;
          if (c) {
            nextBall = { ...bPrev, pos: { x: c.pos.x + BALL_OFFSET.x, y: c.pos.y + BALL_OFFSET.y } };
          }
        } else {
          if (!ballTakenThisFrame) {
            const target = nextPlayers.find((p) => p.id === bPrev.targetId) || null;
            const isSoftThrow = (bPrev.speed ?? BALL_SPEED) < BALL_SPEED;
            const defenders = nextPlayers.filter((p) => p.type === "X");

            // fast throw: any collision with defender
            if (!isSoftThrow) {
              const interceptor = defenders.find((x) => dist(bPrev.pos, x.pos) <= FAST_INTERCEPT_DIST);
              if (interceptor) {
                nextPlayers = nextPlayers.map((p) =>
                  p.id === interceptor.id ? { ...p, hasBall: true } : p.hasBall ? { ...p, hasBall: false } : p
                );
                nextBall = { inFlight: false, pos: { x: interceptor.pos.x, y: interceptor.pos.y }, targetId: null, fromId: null };
                setShowInterceptionUI(true);
                endPlayImmediate("interception");
                forceEndThisFrame = true;
                ballTakenThisFrame = true;
              }
            }

            // lob throw interceptions
            if (!forceEndThisFrame && isSoftThrow) {
              const targetNow = nextPlayers.find((p) => p.id === bPrev.targetId);
              const interceptor = defenders.find((x) => {
                if (
                  targetNow &&
                  dist(x.pos, targetNow.pos) <= LOB_INTERCEPT_RADIUS &&
                  dist(bPrev.pos, x.pos) <= FAST_INTERCEPT_DIST
                )
                  return true;
                return false;
              });

              if (interceptor) {
                nextPlayers = nextPlayers.map((p) =>
                  p.id === interceptor.id ? { ...p, hasBall: true } : p.hasBall ? { ...p, hasBall: false } : p
                );
                nextBall = { inFlight: false, pos: { x: interceptor.pos.x, y: interceptor.pos.y }, targetId: null, fromId: null };
                setShowInterceptionUI(true);
                endPlayImmediate("interception");
                forceEndThisFrame = true;
                ballTakenThisFrame = true;
              }
            }

            // continue normal throw-to-target
            if (!forceEndThisFrame) {
              if (!target) {
                nextBall = { ...bPrev, inFlight: false, targetId: null, fromId: null, speed: undefined };
              } else {
                const targetPos: Vec = { x: target.pos.x + BALL_OFFSET.x, y: target.pos.y + BALL_OFFSET.y };
                const dx = targetPos.x - bPrev.pos.x;
                const dy = targetPos.y - bPrev.pos.y;
                const d = Math.hypot(dx, dy);

                if (d < 6) {
                  nextPlayers = nextPlayers.map((p) =>
                    p.id === target.id ? { ...p, hasBall: true } : p.hasBall ? { ...p, hasBall: false } : p
                  );
                  nextBall = { inFlight: false, pos: targetPos, targetId: null, fromId: null, speed: undefined };
                } else {
                  const speed = (bPrev.speed ?? BALL_SPEED) * speedMultiplier;
                  const step = speed * dt;
                  const t = step >= d ? 1 : step / d;
                  nextBall = { ...bPrev, pos: { x: bPrev.pos.x + dx * t, y: bPrev.pos.y + dy * t } };
                }
              }
            }
          }
        }

        // APPLY BOUNCE IMPULSES (FINAL STEP)
        const impulses = bounceImpulseRef.current;
        if (Object.keys(impulses).length) {
          const stepScale = Math.min(dt * 60, 1);

          nextPlayers = nextPlayers.map((p) => {
            const imp = impulses[p.id];
            if (!imp) return p;

            const mag = Math.hypot(imp.x, imp.y);
            if (mag < 0.06) {
              delete impulses[p.id];
              return p;
            }

            const stepX = clamp((imp.x * stepScale) / BOUNCE_DAMPING, -MAX_BOUNCE_STEP, MAX_BOUNCE_STEP);
            const stepY = clamp((imp.y * stepScale) / BOUNCE_DAMPING, -MAX_BOUNCE_STEP, MAX_BOUNCE_STEP);

            return {
              ...p,
              pos: {
                x: clamp(p.pos.x + stepX, 8, FIELD_WIDTH - 8),
                y: clamp(p.pos.y + stepY, 8, FIELD_HEIGHT - 8),
              },
            };
          });

          const decay = 1 - dt * 1.2;
          for (const id in impulses) impulses[id] = { x: impulses[id].x * decay, y: impulses[id].y * decay };
        }

        // TACKLE / HIT BOUNCE
        const carrierAfter = nextPlayers.find((p) => p.hasBall) || null;

        if (carrierAfter && !nextBall.inFlight && !snapImmunityRef.current) {
          let tackler: Player | null = null;
          let best = Infinity;

          for (const p of nextPlayers) {
            if (p.type !== "X") continue;
            const d = dist(p.pos, carrierAfter.pos);
            if (d < TACKLE_DIST && d < best) {
              best = d;
              tackler = p;
            }
          }

          if (tackler) {
            
            if (!tackleLockRef.current) {
              tackleLockRef.current = true;
              tackleHitsRef.current = 0;
            }

            const now = performance.now();
            if (now - lastHitTimeRef.current > 75) {
              hitFlashRef.current[carrierAfter.id] = now;
              hitFlashRef.current[tackler.id] = now;
              lastHitTimeRef.current = now;
              tackleHitsRef.current += 1;

              const dx = carrierAfter.pos.x - tackler.pos.x;
              const dy = carrierAfter.pos.y - tackler.pos.y;
              const d = Math.hypot(dx, dy) || 1;
              const nx = dx / d;
              const ny = dy / d;

              const carrierPrev = playersRef.current.find(p => p.id === carrierAfter.id);

              const intent = carrierPrev
                ? normalize({
                    x: carrierAfter.pos.x - carrierPrev.pos.x,
                    y: carrierAfter.pos.y - carrierPrev.pos.y,
                  })
                : { x: 0, y: -1 };

                // hit direction (tackler -> carrier)
                const hitDir = { x: nx, y: ny };

                // how aligned the hit is with runner direction
                const dot = hitDir.x * intent.x + hitDir.y * intent.y;

                // lateral component (side hits feel real)
                const lateral = {
                  x: hitDir.x - intent.x * dot,
                  y: hitDir.y - intent.y * dot,
                };

                // FINAL carrier impulse:
                // - small backward slowdown
                // - strong lateral shove
                bounceImpulseRef.current[carrierAfter.id] = {
                  x: intent.x * -BOUNCE_PUSH * 0.12 + lateral.x * BOUNCE_PUSH * 0.45,
                  y: intent.y * -BOUNCE_PUSH * 0.12 + lateral.y * BOUNCE_PUSH * 0.45,
                };


                bounceImpulseRef.current[tackler.id] = { x: -nx * (BOUNCE_PUSH * 0.55), y: -ny * (BOUNCE_PUSH * 0.55) };

              if (tackleHitsRef.current >= MAX_TACKLE_HITS) {
                tackleHitsRef.current = 0;

                const ballYNow = carrierAfter.pos.y + BALL_OFFSET.y;
                const inBotEndzone = ballYNow + BALL_RADIUS >= BOT_ENDZONE_Y;

                if (inBotEndzone) {
                  playOutcomeRef.current = "safety";
                } else {
                  playOutcomeRef.current = "tackle";
                }
              }
            }
          }
        }

        // LOS CROSSING TRACK (BALL-BASED)
        if (!hasBallCrossedLOSRef.current) {
          const b = nextBall;
          if (!b.inFlight && b.pos.y < losYRef.current) {
            hasBallCrossedLOSRef.current = true;
          }
        }

        // OUT OF BOUNDS / SAFETY / SCORE CHECK
        if (!forceEndThisFrame) {
          const carrierAfter2 = nextPlayers.find((p) => p.hasBall) || null;

          if (carrierAfter2 && !nextBall.inFlight) {
            const x = carrierAfter2.pos.x;
            const y = carrierAfter2.pos.y + BALL_OFFSET.y;

            if ((isOutOfBoundsX(x) && isInBottomEndzone(y)) || isOutBottom(y)) {
              playOutcomeRef.current = "safety";
            }
            else if (isBallTouchingTopGoalLine(y)) {
              setScore((s) => s + 6);
              setShowScoreUI(true);
              tackleHitsRef.current = 0;
              setDown(1);
              playOutcomeRef.current = "touchdown";
            } 
            else if (isOutOfBoundsX(x)) {
              playOutcomeRef.current = "tackle";
            }
          }
        }
      }

      // commit
      playersRef.current = nextPlayers;
      ballRef.current = nextBall;
      setPlayers(nextPlayers);
      setBall(nextBall);

      // apply play outcome (once)
      if (playOutcomeRef.current !== "none") {
        const outcome = playOutcomeRef.current;
        playOutcomeRef.current = "none";

        if (outcome === "interception" || outcome === "turnover" || outcome === "safety") {
          if (outcome === "safety") setShowSafetyUI(true);
          loseLifeAndReset();
          return;
        }

        if (outcome === "touchdown") {
          setSpeedLevel((lvl) =>
            Math.min(lvl + 1, SPEED_MULTIPLIERS.length - 1)
          );
          resetToStart();
          return;
        }

        if (outcome === "tackle") {
          setDown((d) => {
            if (d >= MAX_DOWNS) {
              loseLifeAndReset();
              return 1;
            }

            // normal down advance
            endPlayAt(carrierNext?.pos.y ?? FIELD_HEIGHT / 2);
            return d + 1;
          });

          return;
        }
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lastTime.current = null;
    };
  }, [running, playLive, endPlayAt, loseLifeAndReset, resetToStart, endPlayImmediate]);

  /* ---------------- DRAW ROUTE (MOVE/UP) ---------------- */

  useEffect(() => {
    if (!drawingFor) return;

    const handleMove = (e: MouseEvent) => {
      if (!running) return;
      if (!drawingFor) return;

      const rect = fieldRef.current?.getBoundingClientRect();
      if (!rect) return;

      const pt = {
        x: clamp(e.clientX - rect.left, 0, FIELD_WIDTH),
        y: clamp(e.clientY - rect.top, 0, FIELD_HEIGHT),
      };

      const next = playersRef.current.map((p) => {
        if (p.id !== drawingFor) return p;

        const last = p.route[p.route.length - 1];
        if (last && dist(last, pt) < 3) return p;

        return { ...p, route: [...p.route, pt] };
      });

      commitPlayers(next);
    };

    const handleUp = () => {
      drawingLockRef.current = null;
      setDrawingFor(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [commitPlayers, drawingFor, running]);

  /* ---------------- ROUTE DRAWING (LOCAL POINT) ---------------- */

  const getLocalPoint = useCallback((e: React.MouseEvent): Vec => {
    const rect = fieldRef.current!.getBoundingClientRect();
    return { x: clamp(e.clientX - rect.left, 0, FIELD_WIDTH), y: clamp(e.clientY - rect.top, 0, FIELD_HEIGHT) };
  }, []);

  // kept for parity (unused in JSX right now)
  const drawRoute = useCallback(
    (e: React.MouseEvent) => {
      if (!running) return;
      if (!drawingFor) return;

      const pt = getLocalPoint(e);

      setPlayers((prev) =>
        prev.map((p) => {
          if (p.id !== drawingFor) return p;

          const clampedPt = { x: clamp(pt.x, 0, FIELD_WIDTH), y: clamp(pt.y, 0, FIELD_HEIGHT) };

          const last = p.route[p.route.length - 1];
          if (last && dist(last, clampedPt) < 3) return p;

          return { ...p, route: [...p.route, clampedPt] };
        })
      );
    },
    [drawingFor, getLocalPoint, running]
  );

  /* ---------------- CANVAS RENDER ---------------- */

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, FIELD_WIDTH, FIELD_HEIGHT);
    ctx.strokeStyle = "rgba(167,139,250,0.85)";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    players.forEach((p) => {
      if (p.type !== "O") return;
      if (p.route.length < 1) return;

      ctx.beginPath();
      ctx.moveTo(p.pos.x, p.pos.y);
      p.route.forEach((pt) => ctx.lineTo(pt.x, pt.y));
      ctx.stroke();

      // tiny glow cap
      const last = p.route[p.route.length - 1];
      ctx.beginPath();
      ctx.arc(last.x, last.y, 3, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(216,180,254,0.9)";
      ctx.fill();
    });
  }, [players]);

  /* ---------------- FIELD MARKINGS ---------------- */

  const FieldMarkings = useMemo(() => {
    const els: React.ReactNode[] = [];
    // hash marks (50px off center)
    const centerX = FIELD_WIDTH / 2;
    const leftHashX = centerX - HASH_OFFSET_FROM_CENTER;
    const rightHashX = centerX + HASH_OFFSET_FROM_CENTER;

    // hashes every yard (skip endzones)
    for (let yard = 1; yard < FIELD_YARDS; yard++) {
      const y = yFromYards(ENDZONE_YARDS + yard);

      els.push(
        <div
          key={`hash-L-${yard}`}
          className="absolute pointer-events-none"
          style={{
            left: leftHashX - HASH_WIDTH / 2,
            top: y - HASH_HEIGHT / 2,
            width: HASH_WIDTH,
            height: HASH_HEIGHT,
            background: "rgba(255,255,255,0.55)",
            boxShadow: "0 0 6px rgba(167,139,250,0.35)",
          }}
        />
      );

      els.push(
        <div
          key={`hash-R-${yard}`}
          className="absolute pointer-events-none"
          style={{
            left: rightHashX - HASH_WIDTH / 2,
            top: y - HASH_HEIGHT / 2,
            width: HASH_WIDTH,
            height: HASH_HEIGHT,
            background: "rgba(255,255,255,0.55)",
            boxShadow: "0 0 6px rgba(167,139,250,0.35)",
          }}
        />
      );
    }

    const topGoalLine = yFromYards(ENDZONE_YARDS);
    const midLine = yFromYards(ENDZONE_YARDS + 50);
    const botGoalLine = yFromYards(ENDZONE_YARDS + FIELD_YARDS);

    // endzones
    els.push(
      <div
        key="ez-top"
        className="absolute left-0 top-0 w-full"
        style={{
          height: topGoalLine,
          background:
            "linear-gradient(to bottom, rgba(124,58,237,0.22), rgba(2,6,23,0.0) 70%)",
        }}
      />
    );
    els.push(
      <div
        key="ez-bot"
        className="absolute left-0 w-full"
        style={{
          top: botGoalLine,
          height: FIELD_HEIGHT - botGoalLine,
          background:
            "linear-gradient(to top, rgba(124,58,237,0.22), rgba(2,6,23,0.0) 70%)",
        }}
      />
    );

    // goal lines + midfield
    els.push(
      <div
        key="goal-top"
        className="absolute left-0 w-full"
        style={{ top: topGoalLine, borderTop: "3px solid rgba(255,255,255,0.75)" }}
      />
    );
    els.push(
      <div
        key="goal-bot"
        className="absolute left-0 w-full"
        style={{ top: botGoalLine, borderTop: "3px solid rgba(255,255,255,0.75)" }}
      />
    );
    els.push(
      <div
        key="mid"
        className="absolute left-0 w-full"
        style={{ top: midLine, borderTop: "2px solid rgba(255,255,255,0.55)" }}
      />
    );

        /* ---------------- ENDZONE TEXT ---------------- */

    const ENDZONE_TEXT = "CALCERABALL";

    // TOP ENDZONE (rotated like real fields)
    els.push(
      <div
        key="endzone-text-top"
        className="absolute left-1/2 pointer-events-none select-none"
        style={{
          top: topGoalLine / 2,
          transform: "translate(-50%, -50%) rotate(0deg)",
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: "0.38em",
          color: "rgba(255, 255, 255, 0.78)",
          textShadow:
            "0 0 18px rgba(167,139,250,0.55), 0 0 36px rgba(124,58,237,0.35)",
          opacity: 0.85,
          whiteSpace: "nowrap",
        }}
      >
        {ENDZONE_TEXT}
      </div>
    );

    // BOTTOM ENDZONE
    els.push(
      <div
        key="endzone-text-bot"
        className="absolute left-1/2 pointer-events-none select-none"
        style={{
          top: botGoalLine + (FIELD_HEIGHT - botGoalLine) / 2,
          transform: "translate(-50%, -50%)",
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: "0.38em",
          color: "rgba(255, 255, 255, 0.78)",
          textShadow:
            "0 0 18px rgba(167,139,250,0.55), 0 0 36px rgba(124,58,237,0.35)",
          opacity: 0.85,
          whiteSpace: "nowrap",
        }}
      >
        {ENDZONE_TEXT}
      </div>
    );

    // 10-40 + 60-90 lines and labels
    for (let fieldYards = 10; fieldYards <= 90; fieldYards += 10) {
      const y = yFromYards(ENDZONE_YARDS + fieldYards);
      if (fieldYards === 50) continue;

      els.push(
        <div
          key={`yd-${fieldYards}`}
          className="absolute left-0 w-full"
          style={{ top: y, borderTop: "1px solid rgba(255,255,255,0.18)" }}
        />
      );

      const label = yardLabelFromFieldYards(fieldYards);
      const isSmall = label !== 50;
      const offset = isSmall ? YARD_NUM_OFFSET_SMALL : YARD_NUM_OFFSET_BIG;

      const common: React.CSSProperties = {
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
        textShadow: "0 0 10px rgba(167,139,250,0.4)",
      };

      // left digits
      els.push(
        <span
          key={`lbl-${fieldYards}-L-top`}
          className="absolute left-3 text-white/65 pointer-events-none select-none"
          style={{
            ...common,
            top: y - offset,
            fontSize: isSmall ? 11 : 18,
            fontWeight: label === 50 ? 800 : 650,
            transform: "translateY(-55%) rotate(90deg)",
            transformOrigin: "center",
          }}
        >
          {String(label)[0]}
        </span>
      );
      els.push(
        <span
          key={`lbl-${fieldYards}-L-bot`}
          className="absolute left-3 text-white/65 pointer-events-none select-none"
          style={{
            ...common,
            top: y + offset,
            fontSize: isSmall ? 11 : 18,
            fontWeight: label === 50 ? 800 : 650,
            transform: "translateY(-40%) rotate(90deg)",
            transformOrigin: "center",
          }}
        >
          {String(label)[1]}
        </span>
      );

      // right digits
      els.push(
        <span
          key={`lbl-${fieldYards}-R-top`}
          className="absolute right-3 text-white/65 pointer-events-none select-none"
          style={{
            ...common,
            top: y - offset,
            fontSize: isSmall ? 11 : 18,
            fontWeight: label === 50 ? 800 : 650,
            transform: "translateY(10%) rotate(-90deg)",
            transformOrigin: "center",
          }}
        >
          {String(label)[0]}
        </span>
      );
      els.push(
        <span
          key={`lbl-${fieldYards}-R-bot`}
          className="absolute right-3 text-white/65 pointer-events-none select-none"
          style={{
            ...common,
            top: y + offset,
            fontSize: isSmall ? 11 : 18,
            fontWeight: label === 50 ? 800 : 650,
            transform: "translateY(-110%) rotate(-90deg)",
            transformOrigin: "center",
          }}
        >
          {String(label)[1]}
        </span>
      );
    }

    // midfield 50
    els.push(
      <span
        key="50-left"
        className="absolute left-3 pointer-events-none select-none"
        style={{
          top: midLine,
          transform: "translateX(-40%) translateY(-45%) rotate(90deg)",
          fontSize: 15,
          fontWeight: 900,
          color: "rgba(255,255,255,0.92)",
          textShadow: "0 0 14px rgba(167,139,250,0.65)",
        }}
      >
        5 0
      </span>
    );
    els.push(
      <span
        key="50-right"
        className="absolute right-3 pointer-events-none select-none"
        style={{
          top: midLine,
          transform: "translateX(40%) translateY(-50%) rotate(-90deg)",
          fontSize: 15,
          fontWeight: 900,
          color: "rgba(255,255,255,0.92)",
          textShadow: "0 0 14px rgba(167,139,250,0.65)",
        }}
      >
        5 0
      </span>
    );

    return els;
  }, []);

  /* ---------------- UI ACTIONS ---------------- */

  const handleReset = () => {
    setGameOver(false);
    setScore(0);
    setLives(MAX_LIVES);
    setDown(1);
    setShowScoreUI(false);
    setSpeedLevel(0);
    setRunning(false);
    setPlayLive(false);
    setCountdown(null);
    setDrawingFor(null);
    lastTime.current = null;

    tackleHitsRef.current = 0;
    lastHitTimeRef.current = 0;
    playOutcomeRef.current = "none";

    const restoredPlayers = initialPlayersRef.current.map((p) => ({
      ...p,
      pos: { ...p.pos },
      route: [],
      hasBall: p.hasBall,
    }));

    playersRef.current = restoredPlayers;
    setPlayers(restoredPlayers);

    const carrier = restoredPlayers.find((p) => p.hasBall);
    setBall({
      inFlight: false,
      pos: carrier
        ? { x: carrier.pos.x + BALL_OFFSET.x, y: carrier.pos.y + BALL_OFFSET.y }
        : { x: FIELD_WIDTH / 2, y: FIELD_HEIGHT / 2 },
      targetId: null,
      fromId: null,
    });
  };

  const startPlay = () => {
    if (running || gameOver) return;

    tackleHitsRef.current = 0;
    lastHitTimeRef.current = 0;
    hasBallCrossedLOSRef.current = false;
    bounceImpulseRef.current = {};

    setRunning(true);
    setCountdown(countdownTime);
    setPlayLive(false);
    lastTime.current = null;
  };

  /* ---------------- FUTURISTIC HUD METRICS ---------------- */

  const momentum = useMemo(() => {
    // simple "fun" meter: score influences, but clamps
    const base = clamp01(score / 36);
    const lifePenalty = (MAX_LIVES - lives) * 0.12;
    const downPenalty = (down - 1) * 0.08;
    return clamp01(base - lifePenalty - downPenalty + 0.25);
  }, [score, lives, down]);

  const ydsToFirst = useMemo(() => {
    // lightweight placeholder that still feels "real"
    // (keeps your gameplay logic unchanged)
    const base = 10;
    const mod = (down - 1) * 2;
    return Math.max(1, base + mod);
  }, [down]);

  const possession = useMemo(() => 50, []);

  /* ---------------- RENDER ---------------- */

  return (
    <div className="h-full w-full flex items-center justify-center">
      {/* Local styles for the HUD effects */}
      <style>{`
        @keyframes cb-scan {
          0% { transform: translateY(-40%); opacity: 0; }
          10% { opacity: 0.55; }
          50% { opacity: 0.22; }
          100% { transform: translateY(140%); opacity: 0; }
        }
        @keyframes cb-flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.92; }
        }
        @keyframes cb-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
      `}</style>

      <div className="flex flex-col items-center gap-4">
        
        {/* MAIN LAYOUT: left spacer, field, right stats */}
        <div className="flex items-start gap-4">

          {/* FIELD WRAPPER */}
          <div className="relative" style={{ width: FIELD_WIDTH }}>
            {/* FIELD */}
            <div
              ref={fieldRef}
              className="relative rounded-[28px] overflow-hidden cursor-pointer"
              style={{
                width: FIELD_WIDTH,
                height: FIELD_HEIGHT,
                background:
                  "radial-gradient(700px 460px at 20% 15%, rgba(124,58,237,0.30), rgba(2,6,23,0.10) 55%), radial-gradient(640px 520px at 80% 80%, rgba(59,130,246,0.12), rgba(2,6,23,0.05) 60%), linear-gradient(180deg, rgba(17,24,39,0.58), rgba(2,6,23,0.55))",
                border: "1px solid rgba(216,180,254,0.16)",
                boxShadow:
                  "0 0 0 1px rgba(124,58,237,0.12), 0 28px 90px rgba(2,6,23,0.75), 0 0 42px rgba(124,58,237,0.22)",
                backdropFilter: "blur(16px)",
              }}
              onClick={() => {
                if (!running) startPlay();
              }}
            >
              {/* hologram frame corners */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(220px 220px at 18% 12%, rgba(255,255,255,0.10), transparent 60%), radial-gradient(220px 220px at 84% 88%, rgba(255,255,255,0.08), transparent 62%)",
                }}
              />

              {/* scanline */}
              <div
                className="absolute left-0 top-0 w-full pointer-events-none"
                style={{
                  height: 180,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.0), rgba(216,180,254,0.20), rgba(255,255,255,0.0))",
                  filter: "blur(0.2px)",
                  animation: "cb-scan 4.5s linear infinite",
                  mixBlendMode: "screen",
                }}
              />

              {/* interception/safety/score overlays */}
              {showInterceptionUI && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                  <div
                    className="px-6 py-3 rounded-2xl text-white font-black text-3xl"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(244,63,94,0.26), rgba(2,6,23,0.35))",
                      border: "1px solid rgba(251,113,133,0.22)",
                      boxShadow: "0 0 46px rgba(244,63,94,0.55)",
                      textShadow: "0 0 22px rgba(244,63,94,0.95)",
                    }}
                  >
                    INTERCEPTION
                  </div>
                </div>
              )}

              {showSafetyUI && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                  <div
                    className="px-6 py-3 rounded-2xl text-white font-black text-3xl"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(239,68,68,0.24), rgba(2,6,23,0.35))",
                      border: "1px solid rgba(248,113,113,0.20)",
                      boxShadow: "0 0 46px rgba(239,68,68,0.5)",
                      textShadow: "0 0 22px rgba(239,68,68,0.9)",
                    }}
                  >
                    SAFETY
                  </div>
                </div>
              )}

              {showScoreUI && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                  <div
                    className="px-6 py-3 rounded-2xl text-white font-black text-4xl"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(34,197,94,0.22), rgba(2,6,23,0.35))",
                      border: "1px solid rgba(134,239,172,0.18)",
                      boxShadow: "0 0 46px rgba(34,197,94,0.45)",
                      textShadow: "0 0 22px rgba(34,197,94,0.9)",
                    }}
                  >
                    SCORE!
                  </div>
                </div>
              )}

              {FieldMarkings}

              <canvas
                ref={canvasRef}
                width={FIELD_WIDTH}
                height={FIELD_HEIGHT}
                className="absolute inset-0"
              />

              

              {gameOver && (
                <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
                  <div
                    className="px-8 py-6 rounded-3xl text-center"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0.9), rgba(2,6,23,0.95))",
                      border: "1px solid rgba(216,180,254,0.25)",
                      boxShadow:
                        "0 0 60px rgba(239,68,68,0.45), inset 0 0 30px rgba(124,58,237,0.25)",
                      backdropFilter: "blur(18px)",
                    }}
                  >
                    <div
                      className="text-4xl font-black text-red-400 mb-2"
                      style={{ textShadow: "0 0 24px rgba(239,68,68,0.8)" }}
                    >
                      GAME OVER
                    </div>
                    <div className="text-white/80">
                      Final Score: <b>{score}</b>
                    </div>
                  </div>
                </div>
              )}

              {/* PLAY BUTTON OVERLAY */}
              {!running && countdown === null && (
                
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startPlay();
                    }}
                    className="relative flex items-center justify-center w-[85px] h-[85px] rounded-full transition-transform duration-150 hover:scale-105 active:scale-95"
                    aria-label="Start Play"
                    style={{
                      background:
                        "radial-gradient(38px 38px at 35% 30%, rgba(255,255,255,0.25), rgba(124,58,237,0.95) 55%, rgba(88,28,135,0.85) 100%)",
                      border: "5px solid rgba(195, 0, 255, 0.81)",
                      boxShadow:
                        "0 0 0 1px rgba(124,58,237,0.16), 0 0 56px rgba(124,58,237,0.75), 0 24px 70px rgba(2,6,23,0.65)",
                      animation: "cb-pulse 2.4s ease-in-out infinite",
                    }}
                  >
                    {/* outer ring */}
                    <div
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{
                        boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.08)",
                      }}
                    />
                    <div
                      style={{
                        width: 0,
                        height: 0,
                        marginLeft: 6,
                        borderTop: "13px solid transparent",
                        borderBottom: "13px solid transparent",
                        borderLeft: "20px solid rgba(255,255,255,0.96)",
                        filter: "drop-shadow(0 0 10px rgba(255,255,255,0.55))",
                      }}
                    />
                  </button>
                </div>
              )}

              {/* players */}
              {players.map((p) => {
                const isO = p.type === "O";

                return (
                  <div
                    key={p.id}
                    className="absolute font-black select-none"
                    
                    style={{
                      left: p.pos.x,
                      top: p.pos.y,
                      transform: "translate(-50%, -50%)",
                      borderRadius: "50%",
                      fontSize: isO ? O_FONT : X_FONT,
                      color: isO ? O_COLOR : X_COLOR,
                      cursor: isO ? "pointer" : "default",
                      textShadow: `${isO ? O_GLOW : X_GLOW}, ${OUTLINE}`,
                      letterSpacing: isO ? 0.7 : 0.4,
                      pointerEvents: isO ? "auto" : "none", // ⭐ IMPORTANT
                      zIndex: isO ? 3 : 2,                  // ⭐ IMPORTANT
                    }}
                    onMouseDown={() => {
                      if (!isO) return;
                      if (!running) return;

                      pressStartRef.current = performance.now();
                      pressPlayerRef.current = p.id;

                      clickTimerRef.current = window.setTimeout(() => {
                        drawingLockRef.current = p.id;

                        const cleared = playersRef.current.map((pl) =>
                          pl.id === p.id ? { ...pl, route: [] } : pl
                        );
                        commitPlayers(cleared);

                        setDrawingFor(p.id);
                      }, HOLD_TO_DRAW_MS);
                    }}
                    title={isO ? "Hold to draw route. Release quickly to throw." : ""}
                  >
                    {p.type}
                  </div>
                );
              })}

              {/* football */}
              <div
                className="absolute"
                style={{
                  left: ball.inFlight
                    ? ball.pos.x
                    : ballCarrier
                    ? ballCarrier.pos.x + BALL_OFFSET.x
                    : FIELD_WIDTH / 2,
                  top: ball.inFlight
                    ? ball.pos.y
                    : ballCarrier
                    ? ballCarrier.pos.y + BALL_OFFSET.y
                    : FIELD_HEIGHT / 2,
                  transform: "translate translate(-50%, -50%) rotate(20deg)",
                  width: 10,
                  height: 18,
                  background:
                    "linear-gradient(180deg, rgba(199, 86, 11, 0.95), rgba(134, 46, 2, 0.95))",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.45)",
                  border: "1px solid rgba(0,0,0,0.18)",
                  pointerEvents: "none",
                  zIndex: 5,
                  clipPath:
                    "polygon(50% 0%, 78% 12%, 100% 50%, 78% 88%, 50% 100%, 22% 88%, 0% 50%, 22% 12%)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 2,
                    height: 12,
                    background: "rgba(255,255,255,0.75)",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 6,
                    height: 1,
                    background: "rgba(255,255,255,0.55)",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "40%",
                    width: 6,
                    height: 1,
                    background: "rgba(255,255,255,0.55)",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "60%",
                    width: 6,
                    height: 1,
                    background: "rgba(255,255,255,0.55)",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                  }}
                />
              </div>

              
            </div>
            
          </div>

          {/* RIGHT HUD PANEL */}
          <div
            className="w-[220px] rounded-2xl p-3"
            style={{
              background:
                "linear-gradient(180deg, rgba(17,24,39,0.62), rgba(2,6,23,0.45))",
              border: "1px solid rgba(216,180,254,0.12)",
              boxShadow: "0 14px 44px rgba(2,6,23,0.55)",
              backdropFilter: "blur(14px)",
            }}
          >

            <div
              className="rounded-2xl p-3 mb-3"
              style={{
                background:
                  "linear-gradient(180deg, rgba(124,58,237,0.16), rgba(2,6,23,0.18))",
                border: "1px solid rgba(216,180,254,0.12)",
              }}
            >
              <div className="flex items-end justify-between">
                <div className="text-white text-[11px] tracking-[0.12em]">SCORE</div>
                <div
                  className="text-green-400 font-black"
                  style={{
                    fontSize: 30,
                    letterSpacing: "0.16em",
                    textShadow: "0 0 20px rgba(167,139,250,0.55)",
                  }}
                >
                  {pad3(score)}
                </div>
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between text-white text-[11px] tracking-[0.12em]">
                  <span>SPEED MULTIPLIER</span>
                  <span className="text-green-300">{Math.round((speedMultiplier - 1) * 100)}%</span>
                </div>              
              </div>
            </div>
            
            <div
                className="rounded-2xl p-3 mb-3"
                style={{
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(248,113,113,0.18)",
                  boxShadow: "0 0 22px rgba(239,68,68,0.18)",
                }}
              >
                <div className="text-white/55 text-[11px] tracking-[0.12em]">LIVES</div>
                <div
                  className="text-white font-black"
                  style={{
                    fontSize: 24,
                    textShadow: "0 0 14px rgba(239,68,68,0.6)",
                  }}
                >
                  {lives}
                </div>
              </div>
              <div
                className="rounded-2xl p-3 mb-3"
                style={{
                  background: "rgba(59,130,246,0.08)",
                  border: "1px solid rgba(147,197,253,0.16)",
                  boxShadow: "0 0 22px rgba(59,130,246,0.14)",
                }}
              >
                <div className="text-white/55 text-[11px] tracking-[0.12em]">DOWN</div>
                <div className="text-white font-black text-lg">
              {down}
              <span className="text-white/45 font-semibold text-sm">
                {" "} / {MAX_DOWNS}
              </span>
            </div>
              
            </div>  
            <div
                className="rounded-2xl p-3"
                style={{
                  background: "rgba(2,6,23,0.28)",
                  border: "1px solid rgba(216,180,254,0.10)",
                }}
              >
                <div className="text-white/55 text-[11px] tracking-[0.12em]">CONTROLS</div>
                <div className="mt-2 text-white/55 text-xs leading-relaxed">
                  - Hold an O to draw its route
                  <br />
                  - Click an O to lob the ball
                  <br />
                  - Double click O to fast throw
                </div>
                
              </div>  

              
          {/* COUNTDOWN */}
            <div className="flex justify-center mt-3">
            <div
              className="h-[44px] w-[80px] rounded-xl font-black select-none flex items-center justify-center"
              style={{
                fontSize: 25,
                color: "#ffffffff",
                background:
                  "linear-gradient(180deg, rgba(239,68,68,0.22), rgba(2,6,23,0.75))",
                border: "1px solid rgba(248,113,113,0.35)",
                boxShadow:
                  "0 0 24px rgba(239,68,68,0.55), inset 0 0 16px rgba(239,68,68,0.25)",
                textShadow: "0 0 18px rgba(239,68,68,0.95)",
                fontVariantNumeric: "tabular-nums",
                animation: countdown !== null ? "cb-flicker 1.4s infinite" : "none",
              }}
            >
              {countdownDisplay}
            </div>
          </div>

            <div className="flex justify-center mt-2">
              <button
                className="px-4 py-2 rounded-xl text-white text-sm font-semibold"
                onClick={handleReset}
                style={{
                  fontSize: 18,
                  background:
                    "linear-gradient(180deg, rgba(124,58,237,0.85), rgba(88,28,135,0.75))",
                  border: "1px solid rgba(216,180,254,0.22)",
                  boxShadow:
                    "0 0 0 1px rgba(124,58,237,0.18), 0 0 28px rgba(124,58,237,0.35)",
                }}
              >
                Reset
              </button>              
            </div>
          </div>  
                            
        </div>        
      </div>      
    </div>
  );
}
